import { useCallback, useContext, useState } from 'react';
import debounce from 'lodash/debounce';
import LegoAdminPageContext from '../../../pages/legoAdminPageContext';
import useAsyncEffect from './useAsyncEffect';

const cachesByUrl = {};

export default function getServiceUseData(serviceUrl) {
  if(cachesByUrl[serviceUrl]) {
    return cachesByUrl[serviceUrl];
  }

  const documentCache = {};

  // Stores IDs to be fetched in the next debounced API call
  let idsToFetchInNextBatch = new Set();
  // Holds the single promise for the currently pending batch
  let currentBatchPromise = null;

  // We need separate resolve/reject functions accessible by executeBatchFetch
  let resolveCurrentBatchPromise = null;
  let rejectCurrentBatchPromise = null;

  /**
   * Executes the actual API call for a batch of document IDs.
   * Updates the cache and resolves/rejects the single batch promise.
   */
  const executeBatchFetch = (page, serviceUrl) => {
    const idsToFetch = Array.from(idsToFetchInNextBatch);
    idsToFetchInNextBatch.clear(); // Clear pending IDs for the *next* potential batch

    console.log(`%cBatch querying "${serviceUrl}" ${idsToFetch.length} documentIds: %c${idsToFetch.join(', ')}`, 'color: #0BF; font-style: italic;', 'color: #BBB;font-size: 0.7em;');

    const reject = rejectCurrentBatchPromise;
    const resolve = resolveCurrentBatchPromise;

    // Reset the promise so that any future call to this creates its own promise
    currentBatchPromise = null;
    // resolveCurrentBatchPromise = null;
    // rejectCurrentBatchPromise = null;

    if (idsToFetch.length === 0) {
      return
    }
    
    page.service(serviceUrl)
      .find({ query: { _id: { $in: idsToFetch } } })
      .then(results => {    
        const resultMap = new Map(results.map(item => [item._id, item]));      
        idsToFetch.forEach(id => documentCache[id] = resultMap.get(id) || false);
        resolve(true);
      })
      .catch(error => {
        console.error('Document batch query failed:', error);
        reject(error);
      })
  };

  const debouncedFetchData = debounce(executeBatchFetch, 50);

  /**
   * Hook to fetch document data by ID.
   * Uses caching and debounced batching with a single batch promise.
   *
   * @param {string} serviceUrl The service URL to fetch from.
   * @param {string|null} documentId The ID of the document to fetch.
   * @returns {object|null|false} The document data, null if initially loading or no ID, false on error/not found.
   */
  cachesByUrl[serviceUrl] = function(documentId) {
    const { page } = useContext(LegoAdminPageContext);

    const [documentData, setDocumentData] = useState(documentId && (documentCache[documentId] ?? null));

    // Debounce the batch fetch function

    // Fetch the data when the documentId changes
    useAsyncEffect(async (isMounted) => {
      const cachedData = documentCache[documentId];
      if (cachedData) {
        if (isMounted() && documentData !== cachedData) {
          setDocumentData(cachedData);
        }
        return; // Data is cached.
      }

      // Data not cached - Add to batch and manage the single batch promise
      let batchPromiseToAwait = currentBatchPromise;

      idsToFetchInNextBatch.add(documentId);

      if (!batchPromiseToAwait) {
        // No batch currently scheduled, create a new one.
        batchPromiseToAwait = new Promise((resolve, reject) => {
          resolveCurrentBatchPromise = resolve;
          rejectCurrentBatchPromise = reject;
        });
        currentBatchPromise = batchPromiseToAwait;
      }

      debouncedFetchData(page, serviceUrl);
      
      try {
        await batchPromiseToAwait; 
        const finalData = documentCache[documentId];
        isMounted() && setDocumentData(finalData);
      } catch (error) {
        debugger;
        const finalData = documentCache[documentId]; // Should be 'false'
        isMounted() && setDocumentData(finalData); // Update state to 'false'
      }
      
    }, [documentId, page, serviceUrl]); // Effect dependencies
    
    return documentData;
  };

  return cachesByUrl[serviceUrl];
}